/**
 * PriceField: what a host charges per player (drop-ins), per team (tournaments)
 * or per season (leagues). Blank or 0 means free.
 *
 * Shown only while payments are on. A host whose Stripe isn't ready yet is told
 * where to connect it; the server refuses a price until then.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import paymentService from '../../services/api/paymentService';
import { formatMoney } from '../../utils/money';

/** "12.5" → 1250; blank → 0; anything unreadable → NaN */
export const dollarsToCents = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : NaN;
};

export const centsToDollars = (cents) => (cents > 0 ? (cents / 100).toFixed(2) : '');

/** Error text for a price, or '' when it is fine (0, or $1 to $10,000). */
export const priceError = (value) => {
  const cents = dollarsToCents(value);
  if (Number.isNaN(cents)) return 'Enter a price like 12.00';
  if (cents !== 0 && (cents < 100 || cents > 1000000)) return 'Price is $0 (free) or $1 to $10,000';
  return '';
};

/** Payments config and the signed-in host's Stripe status, once. */
export function usePaymentsSetup() {
  const [config, setConfig] = useState(null);
  const [account, setAccount] = useState(null);

  useEffect(() => {
    let live = true;
    paymentService.getConfig()
      .then((c) => {
        if (!live) return;
        setConfig(c);
        if (c?.enabled) {
          paymentService.getConnectStatus().then((a) => live && setAccount(a)).catch(() => live && setAccount(null));
        }
      })
      .catch(() => live && setConfig(null));
    return () => { live = false; };
  }, []);

  return { enabled: Boolean(config?.enabled), ready: Boolean(account?.chargesEnabled) };
}

const noteStyle = { fontSize: '0.78rem', margin: '0.3rem 0 0' };

/**
 * Under a price input: what the payer pays, or the error, or where to
 * connect Stripe. Renders nothing while payments are off.
 */
export function PriceHint({ value, payer = 'Players', hint, setup }) {
  const own = usePaymentsSetup();
  const { enabled, ready } = setup || own;
  const [quote, setQuote] = useState(null);
  const cents = dollarsToCents(value);
  const error = priceError(value);

  useEffect(() => {
    if (!enabled || error || !(cents >= 100)) {
      setQuote(null);
      return undefined;
    }
    const t = setTimeout(() => {
      paymentService.getQuote(cents).then(setQuote).catch(() => setQuote(null));
    }, 300);
    return () => clearTimeout(t);
  }, [cents, error, enabled]);

  if (!enabled) return null;
  if (error) {
    return <p role="alert" style={{ ...noteStyle, color: '#fca5a5' }}>{error}</p>;
  }
  if (!(cents > 0)) {
    return <p style={{ ...noteStyle, color: 'rgba(255,255,255,0.55)' }}>{hint || 'Leave blank for free.'}</p>;
  }
  return (
    <>
      {quote && (
        <p style={{ ...noteStyle, color: 'rgba(255,255,255,0.7)' }}>
          {payer} pay {formatMoney(quote.totalCents, quote.currency)} (card fee included). You get {formatMoney(cents, quote.currency)}.
        </p>
      )}
      {!ready && (
        <p style={{ ...noteStyle, color: '#fcd34d' }}>
          To charge {payer.toLowerCase()}, <Link to="/payments" style={{ color: '#fcd34d' }}>connect Stripe first</Link>.
        </p>
      )}
    </>
  );
}

/** A price input with its hint, shown only while payments are on. */
function PriceField({ value, onChange, label = 'Price per player (CAD)', hint, locked = false, lockedText }) {
  const setup = usePaymentsSetup();
  if (!setup.enabled) return null;

  return (
    <div className="form-group-gaming" data-testid="price-field">
      <label className="form-label-gaming" htmlFor="price-field-input">{label}</label>
      <input
        id="price-field-input"
        type="number"
        min="0"
        step="0.01"
        placeholder="0.00 (free)"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={locked}
        className="form-input-gaming"
        aria-invalid={Boolean(priceError(value))}
      />
      {locked && lockedText && <p style={{ ...noteStyle, color: 'rgba(255,255,255,0.6)' }}>{lockedText}</p>}
      <PriceHint value={value} hint={hint} setup={setup} />
    </div>
  );
}

export default PriceField;
